import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Badge,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import MessageIcon from '@mui/icons-material/Message';
import { useAuth } from '../contexts/AuthContext';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import { useUcpAccess } from '../hooks/useUcpAccess';
import { C } from '../theme';

// Shared style for the plain text nav links: white label, gold-tinted hover.
const navLinkSx = {
  color: C.white,
  fontWeight: 500,
  px: 2,
  py: 1,
  borderRadius: '10px',
  '&:hover': { backgroundColor: C.goldSoft, color: '#F6CB4B' },
} as const;

const menuPaperSx = {
  mt: 1,
  minWidth: 200,
  borderRadius: '14px',
  backgroundColor: C.surface,
  backgroundImage: 'none',
  border: `1px solid ${C.line}`,
  boxShadow: '0 18px 50px rgba(0,0,0,0.5)',
  '& .MuiMenuItem-root': {
    py: 1.4,
    px: 2.5,
    fontSize: '0.95rem',
    color: C.white,
    '&:hover': { backgroundColor: C.goldSoft },
  },
} as const;

const Navbar: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { unreadCount } = useUnreadMessages();
  const { hasAccess: hasUcpAccess } = useUcpAccess();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      setAnchorEl(null);
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/');
      setAnchorEl(null);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => setProfileMenuAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setProfileMenuAnchorEl(null);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: 'rgba(10,10,11,0.82)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderBottom: `1px solid ${C.line}`,
        borderRadius: 0,
        boxShadow: 'none',
        minHeight: '70px',
        width: '100%',
        left: 0,
        top: 0,
        zIndex: 1300,
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ minHeight: '70px', py: 1 }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <RouterLink to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <img src="/logo.png" alt="Nađi Investitora" style={{ height: 48, width: 'auto', marginRight: 12 }} />
            </RouterLink>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button component={RouterLink} to="/" startIcon={<HomeIcon />} sx={navLinkSx}>
                Početna
              </Button>

              <Button component={RouterLink} to="/nekretnine" startIcon={<HomeIcon />} sx={navLinkSx}>
                Nekretnine
              </Button>

              <Button component={RouterLink} to="/business" startIcon={<BusinessIcon />} sx={navLinkSx}>
                Biznis
              </Button>

              {isAuthenticated ? (
                <>
                  {user?.userType === 'land_owner' && (
                    <Button variant="outlined" component={RouterLink} to="/create-ad" sx={{ ml: 0.5 }}>
                      Postavi oglas
                    </Button>
                  )}

                  {user?.userType === 'business_owner' && (
                    <Button variant="outlined" component={RouterLink} to="/create-business-ad" sx={{ ml: 0.5 }}>
                      Postavi oglas za biznis
                    </Button>
                  )}

                  {(user?.userType === 'land_owner' || user?.userType === 'business_owner') && (
                    <Button component={RouterLink} to="/my-ads" sx={navLinkSx}>
                      Moji oglasi
                    </Button>
                  )}

                  <Button
                    component={RouterLink}
                    to="/messages"
                    startIcon={
                      unreadCount > 0 ? (
                        <Badge badgeContent={unreadCount} color="error" max={99}>
                          <MessageIcon />
                        </Badge>
                      ) : (
                        <MessageIcon />
                      )
                    }
                    sx={navLinkSx}
                  >
                    Poruke
                  </Button>

                  <IconButton
                    onClick={handleProfileMenuOpen}
                    sx={{ color: C.white, ml: 0.5, '&:hover': { backgroundColor: C.goldSoft, color: '#F6CB4B' } }}
                  >
                    <PersonIcon />
                  </IconButton>
                  <Menu
                    anchorEl={profileMenuAnchorEl}
                    open={Boolean(profileMenuAnchorEl)}
                    onClose={handleProfileMenuClose}
                    PaperProps={{ sx: menuPaperSx }}
                  >
                    <MenuItem component={RouterLink} to="/profile" onClick={handleProfileMenuClose}>
                      Moj profil
                    </MenuItem>

                    {hasUcpAccess && (
                      <MenuItem component={RouterLink} to="/ucp" onClick={handleProfileMenuClose}>
                        User Control Panel
                      </MenuItem>
                    )}

                    <Divider sx={{ borderColor: C.line }} />
                    <MenuItem onClick={handleLogout} sx={{ color: `${C.danger} !important` }}>
                      Odjavi se
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <>
                  <Button component={RouterLink} to="/login" sx={navLinkSx}>
                    Prijava
                  </Button>
                  <Button variant="contained" component={RouterLink} to="/register" sx={{ ml: 0.5 }}>
                    Registracija
                  </Button>
                </>
              )}
            </Box>
          )}

          {/* Mobile Menu */}
          {isMobile && (
            <>
              <IconButton
                onClick={handleMenuOpen}
                sx={{ color: C.white, p: 1, borderRadius: '10px', '&:hover': { backgroundColor: C.goldSoft } }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{ sx: { ...menuPaperSx, minWidth: 260 } }}
              >
                <MenuItem component={RouterLink} to="/" onClick={handleMenuClose}>
                  <HomeIcon sx={{ mr: 2, color: C.gold }} />
                  Početna
                </MenuItem>

                <MenuItem component={RouterLink} to="/nekretnine" onClick={handleMenuClose}>
                  <HomeIcon sx={{ mr: 2, color: C.gold }} />
                  Nekretnine
                </MenuItem>

                <MenuItem component={RouterLink} to="/business" onClick={handleMenuClose}>
                  <BusinessIcon sx={{ mr: 2, color: C.gold }} />
                  Biznis
                </MenuItem>

                {isAuthenticated ? (
                  <>
                    <Divider sx={{ borderColor: C.line }} />
                    {user?.userType === 'land_owner' && (
                      <MenuItem component={RouterLink} to="/create-ad" onClick={handleMenuClose}>
                        Postavi oglas
                      </MenuItem>
                    )}
                    {user?.userType === 'business_owner' && (
                      <MenuItem component={RouterLink} to="/create-business-ad" onClick={handleMenuClose}>
                        Postavi oglas za biznis
                      </MenuItem>
                    )}
                    {(user?.userType === 'land_owner' || user?.userType === 'business_owner') && (
                      <MenuItem component={RouterLink} to="/my-ads" onClick={handleMenuClose}>
                        Moji oglasi
                      </MenuItem>
                    )}
                    <MenuItem component={RouterLink} to="/messages" onClick={handleMenuClose}>
                      {unreadCount > 0 ? (
                        <Badge badgeContent={unreadCount} color="error" max={99} sx={{ mr: 2 }}>
                          <MessageIcon sx={{ color: C.gold }} />
                        </Badge>
                      ) : (
                        <MessageIcon sx={{ mr: 2, color: C.gold }} />
                      )}
                      Poruke
                    </MenuItem>
                    {hasUcpAccess && (
                      <MenuItem component={RouterLink} to="/ucp" onClick={handleMenuClose}>
                        <PersonIcon sx={{ mr: 2, color: C.gold }} />
                        User Control Panel
                      </MenuItem>
                    )}
                    <Divider sx={{ borderColor: C.line }} />
                    <MenuItem component={RouterLink} to="/profile" onClick={handleMenuClose}>
                      <PersonIcon sx={{ mr: 2, color: C.gold }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: C.white }}>
                        {user?.userType === 'land_owner'
                          ? 'Vlasnik zemljišta'
                          : user?.userType === 'business_owner'
                          ? 'Vlasnik biznisa'
                          : user?.userType === 'investor'
                          ? 'Investitor'
                          : 'Korisnik'}
                      </Typography>
                    </MenuItem>
                    <Divider sx={{ borderColor: C.line }} />
                    <MenuItem onClick={handleLogout} sx={{ color: `${C.danger} !important` }}>
                      Odjavi se
                    </MenuItem>
                  </>
                ) : (
                  <>
                    <Divider sx={{ borderColor: C.line }} />
                    <MenuItem component={RouterLink} to="/login" onClick={handleMenuClose}>
                      Prijava
                    </MenuItem>
                    <MenuItem component={RouterLink} to="/register" onClick={handleMenuClose} sx={{ color: '#F6CB4B', fontWeight: 700 }}>
                      Registracija
                    </MenuItem>
                  </>
                )}
              </Menu>
            </>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
